import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import Button from '@/components/Button';
import { getCurrencySymbol } from '@/constants/countries';
import { RootState } from '@/redux/store';

interface SummaryProps {
  data: any;
  selectedDeliveryType?: string;
  hasQuot: boolean;
  create: () => Promise<void>;
  loading: boolean;
  createDisabled?: boolean;
}

const formatDeliveryTime = (days: number) =>
  days === 0 ? 'Mismo día' : `${days} ${days > 1 ? 'días' : 'día'}`;

const Summary = (props: SummaryProps) => {
  const { data, selectedDeliveryType, hasQuot, create, loading } = props;
  const theme = useTheme();
  const { profile } = useSelector((state: RootState) => state.parcel);
  const currencySymbol = getCurrencySymbol(profile.data?.country);

  const codeAliases: Record<string, string[]> = {
    SMD: ['SMD', 'sameDay'],
    SPT: ['SPT', 'sprint', 'NXD'],
    '99M': ['99M', 'express', 'EXP', '99minutos'],
    CO2F: ['CO2F', 'co2Free', 'CO2'],
    RET: ['RET', 'return', 'retorno'],
    NAL: ['NAL', 'national'],
  };

  // Extract values safely based strictly on the currently selected delivery type
  const activeDelivery =
    Array.isArray(data) && selectedDeliveryType
      ? data.find((item: any) => {
          const itemCode = item?.deliveryTypeCode || item?.deliveryType || item?.code;
          if (!itemCode || !selectedDeliveryType) return false;
          if (itemCode === selectedDeliveryType) return true;
          const aliases = codeAliases[selectedDeliveryType] || [selectedDeliveryType];
          return aliases.includes(itemCode);
        })
      : undefined;

  const hasData = Boolean(data && (!Array.isArray(data) || data.length > 0));
  const hasSelectedCoverage = Boolean(selectedDeliveryType && activeDelivery);
  const isQuotedAndReady = Boolean(hasQuot && hasData && hasSelectedCoverage);

  const shippingCost = isQuotedAndReady ? (activeDelivery?.prices?.flat ?? 0) : 0;
  const insuranceCost = isQuotedAndReady ? (activeDelivery?.prices?.insurance ?? 0) : 0;
  const ivaCost = isQuotedAndReady ? (activeDelivery?.prices?.ivaWithoutCOD ?? 0) : 0;
  const subtotalCost = shippingCost + insuranceCost;
  const totalCost = isQuotedAndReady ? (activeDelivery?.prices?.totalWithoutCOD ?? 0) : 0;
  const etaDays = isQuotedAndReady ? (activeDelivery?.eta?.addedDays ?? 0) : 0;

  // Determine display text for prices and ETA
  const displayShipping = isQuotedAndReady
    ? `${currencySymbol} ${shippingCost.toFixed(2)}`
    : `${currencySymbol} —`;
  const displayInsurance = isQuotedAndReady
    ? `${currencySymbol} ${insuranceCost.toFixed(2)}`
    : `${currencySymbol} —`;
  const displayIva = isQuotedAndReady
    ? `${currencySymbol} ${ivaCost.toFixed(2)}`
    : `${currencySymbol} —`;
  const displaySubtotal = isQuotedAndReady
    ? `${currencySymbol} ${subtotalCost.toFixed(2)}`
    : `${currencySymbol} —`;
  const displayTotal = isQuotedAndReady
    ? `${currencySymbol} ${totalCost.toFixed(2)}`
    : `${currencySymbol} —`;
  const displayEta = isQuotedAndReady ? formatDeliveryTime(etaDays) : 'Por cotizar';

  const isConfirmDisabled = loading || !isQuotedAndReady;

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '16px',
        p: { xxs: 2, md: 2.5 },
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 8px 32px rgba(106, 206, 59, 0.15)',
        color: 'white',
      }}
    >
      <Grid container spacing={{ xxs: 2, md: 3 }} alignItems="center" sx={{ width: '100%' }}>
        {/* Left Panel: Pricing breakdown */}
        <Grid size={{ xxs: 12, md: 5 }}>
          <Box display="flex" flexDirection="column" gap="0.25rem">
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Costo de envío
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {displayShipping}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Seguro
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {displayInsurance}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                IVA
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {displayIva}
              </Typography>
            </Box>

            <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.25)', my: 1 }} />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold">
                Subtotal
                {!hasQuot && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', opacity: 0.7, fontWeight: 'normal' }}
                  >
                    *Completa el formulario para cotizar
                  </Typography>
                )}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {displaySubtotal}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Vertical Divider Line (Hidden on mobile) */}
        <Grid
          size={{ xxs: 0, md: 1 }}
          sx={{ display: { xxs: 'none', md: 'flex' }, justifyContent: 'center' }}
        >
          <Box
            sx={{ height: '110px', width: '1px', backgroundColor: 'rgba(255, 255, 255, 0.25)' }}
          />
        </Grid>

        {/* Right Panel: Highlights (ETA & Total) and Confirm Action */}
        <Grid size={{ xxs: 12, md: 6 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xxs: 12, sm: 6 }}>
              <Box display="flex" flexDirection="column" gap="0.25rem">
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.85,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    fontSize: '0.7rem',
                  }}
                >
                  Tiempo estimado
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {displayEta}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xxs: 12, sm: 6 }}>
              <Box display="flex" flexDirection="column" gap="0.25rem">
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.85,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    fontSize: '0.7rem',
                  }}
                >
                  Total a pagar
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.5px' }}>
                  {displayTotal}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xxs: 12 }} sx={{ mt: 1 }}>
              <Button
                onClick={create}
                disabled={isConfirmDisabled}
                fullWidth
                sx={{
                  backgroundColor: 'white',
                  color: theme.palette.secondary.main,
                  fontWeight: 700,
                  borderRadius: '24px',
                  py: 1.2,
                  fontSize: '1rem',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.12)',
                  textTransform: 'none',
                  border: 'none',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 6px 20px rgba(255, 255, 255, 0.25)',
                    transform: 'translateY(-1px)',
                    border: 'none',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.4)',
                    color: 'rgba(0, 0, 0, 0.35)',
                    boxShadow: 'none',
                  },
                }}
                variant="contained"
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.secondary.main }} />
                ) : !hasQuot || !hasData ? (
                  'Ingresa datos para cotizar'
                ) : !selectedDeliveryType || !activeDelivery ? (
                  'Sin cobertura disponible'
                ) : (
                  `Confirmar orden • ${displayTotal}`
                )}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Summary;
